// La sous-traitance d'une pièce : traitements de surface, traitements
// thermiques, rectification confiée à l'extérieur.
//
// Le calcul la gère en trois modes depuis `shared/pricing.js` et les
// routes la persistent depuis la phase 2.3 ; c'est l'interface qui
// manquait, donc elle n'était pas saisissable.
//
// Le piège du mode « au kilo », et c'est le seul point délicat de cet
// écran : il se calcule sur la masse DES PIÈCES, jamais sur la masse
// ACHETÉE. Quand un format d'achat est renseigné, on paie la barre
// entière (CLAUDE.md §6) — mais un zingage se paie sur ce qui part au
// traitement, pas sur les deux tiers de barre qui partent à la benne.
// L'erreur inverse passerait totalement inaperçue sur un devis.

import NumberField from '../../ui/NumberField.jsx';
import { TextField, Select } from '../../ui/TextField.jsx';
import Button from '../../ui/Button.jsx';
import { formatEuro } from '../../lib/format.js';
import { emptySubcontracting } from './useDevisEditor.js';

const MODE_OPTIONS = [
  { value: 'flat', label: 'Forfait pour le lot' },
  { value: 'per_piece', label: 'Au nombre de pièces' },
  { value: 'per_kg', label: 'Au poids des pièces' },
];

const MODE_UNITS = {
  flat: '€ / lot',
  per_piece: '€ / pc',
  per_kg: '€ / kg',
};

const MODE_HINTS = {
  flat: 'Prix du lot, quelle que soit la quantité.',
  per_piece: 'Multiplié par la quantité.',
  per_kg: 'Sur le poids des pièces, pas sur celui de la barre achetée.',
};

export function SubcontractingTable({ lines, suppliers, breakdown, onChange, disabled }) {
  const patch = (index, values) =>
    onChange(lines.map((sc, i) => (i === index ? { ...sc, ...values } : sc)));

  const supplierOptions = [
    { value: '', label: '—' },
    ...suppliers.map((s) => ({ value: String(s.id), label: s.name })),
  ];

  return (
    <div>
      <div className="row row--between">
        <p className="caps">Sous-traitance</p>
        {/* MÊME bouton que « Ajouter un palier » : même taille, même
            variante. Ils faisaient le même geste — ajouter une ligne à
            un tableau — avec deux apparences différentes, l'un `ghost` et
            l'autre `secondary`. Deux apparences pour un même geste font
            croire à deux natures. */}
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={() => onChange([...lines, emptySubcontracting()])}
        >
          Ajouter une sous-traitance
        </Button>
      </div>

      {lines.length === 0 ? (
        <p className="field__hint">
          Aucune. Ajoutez-en une pour un traitement de surface, un traitement thermique ou une
          opération confiée à l’extérieur.
        </p>
      ) : (
        <div className="stack">
          {lines.map((sc, i) => (
            <div className="op-row" key={sc.id ?? `new-${i}`}>
              <div className="op-row__grid">
                <TextField
                  label="Prestation"
                  value={sc.label ?? ''}
                  disabled={disabled}
                  onChange={(e) => patch(i, { label: e.target.value })}
                />
                <Select
                  label="Fournisseur"
                  value={sc.supplier_id ? String(sc.supplier_id) : ''}
                  options={supplierOptions}
                  disabled={disabled}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : null;
                    const supplier = suppliers.find((s) => s.id === id);
                    // Le délai du fournisseur est PROPOSÉ, pas imposé —
                    // même règle que le taux d'une machine.
                    patch(i, {
                      supplier_id: id,
                      lead_days: supplier?.lead_days ?? sc.lead_days,
                    });
                  }}
                />
                <Select
                  label="Mode"
                  value={sc.mode ?? 'flat'}
                  options={MODE_OPTIONS}
                  disabled={disabled}
                  onChange={(e) => patch(i, { mode: e.target.value })}
                />
                <NumberField
                  label="Prix"
                  unit={MODE_UNITS[sc.mode] ?? '€'}
                  value={sc.unit_price}
                  disabled={disabled}
                  min={0}
                  // Un prix absent ne bloque plus le devis — mais il ne
                  // vaut pas zéro : la ligne sort du total, et ça se dit
                  // ICI, là où on le saisirait.
                  hint={
                    sc.unit_price == null
                      ? 'À confirmer : cette ligne n’entre pas dans le total tant qu’elle est vide.'
                      : MODE_HINTS[sc.mode]
                  }
                  onChange={(v) => patch(i, { unit_price: v })}
                />
                <NumberField
                  label="Minimum de facturation"
                  unit="€"
                  value={sc.min_invoice}
                  disabled={disabled}
                  min={0}
                  hint="Appliqué avant le coefficient."
                  onChange={(v) => patch(i, { min_invoice: v })}
                />
                <NumberField
                  label="Coefficient d'achat"
                  value={sc.purchase_coef}
                  disabled={disabled}
                  min={0}
                  hint="Vide : celui de la pièce s'applique."
                  onChange={(v) => patch(i, { purchase_coef: v })}
                />
                <NumberField
                  label="Délai"
                  unit="jours"
                  value={sc.lead_days}
                  disabled={disabled}
                  min={0}
                  onChange={(v) => patch(i, { lead_days: v })}
                />
              </div>

              <div className="op-row__side">
                <p className="caps">Coût</p>
                <p className="num">{formatEuro(breakdown?.[i]?.cost_total)}</p>
                {/* Le montant avant minimum et coefficient n'est affiché
                    que s'il diffère : sinon c'est du bruit. */}
                {breakdown?.[i] && breakdown[i].base_cost !== breakdown[i].cost_total ? (
                  <p className="field__hint">Base {formatEuro(breakdown[i].base_cost)}</p>
                ) : null}
                <Button
                  variant="ghost"
                  icon
                  aria-label={`Supprimer la sous-traitance ${sc.label || i + 1}`}
                  disabled={disabled}
                  onClick={() => onChange(lines.filter((_, j) => j !== i))}
                >
                  ×
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SubcontractingTable;
